import { OWNER_TIMEZONE } from '@diana/shared'

/**
 * Diana always sees her own timezone; students always see theirs.
 *
 * Every helper here takes an absolute instant and a zone, and never does
 * arithmetic on wall-clock strings. See docs/04-ARCHITECTURE.md §8.
 */

export const browserTimezone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || OWNER_TIMEZONE

export function timeIn(instant: Date | string, timeZone: string): string {
  return new Intl.DateTimeFormat('pl-PL', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(instant))
}

export function dateIn(instant: Date | string, timeZone: string, locale = 'pl-PL'): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(instant))
}

export function dateTimeIn(instant: Date | string, timeZone: string): string {
  return `${dateIn(instant, timeZone)}, ${timeIn(instant, timeZone)}`
}

/** 'YYYY-MM-DD' for an instant, as seen in a given zone. */
export function isoDateIn(instant: Date | string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(instant))
  return parts
}

/* ── calendar grid helpers (pure date arithmetic, no timezones) ────────── */

export type CalendarCell = { iso: string; day: number; inMonth: boolean; isToday: boolean }

const pad = (n: number) => String(n).padStart(2, '0')
export const isoOf = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`

/**
 * Six weeks of cells for a month, Monday-first (Polish convention).
 * Always 42 cells so the grid doesn't jump height between months.
 */
export function monthGrid(year: number, month: number, todayIso: string): CalendarCell[] {
  const first = new Date(Date.UTC(year, month, 1))
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()

  // getUTCDay: 0 = Sunday. Shift so Monday = 0.
  const lead = (first.getUTCDay() + 6) % 7

  const cells: CalendarCell[] = []

  const prevMonthDays = new Date(Date.UTC(year, month, 0)).getUTCDate()
  for (let i = lead - 1; i >= 0; i--) {
    const d = prevMonthDays - i
    const m = month === 0 ? 11 : month - 1
    const y = month === 0 ? year - 1 : year
    cells.push({ iso: isoOf(y, m, d), day: d, inMonth: false, isToday: false })
  }

  for (let d = 1; d <= daysInMonth; d++) {
    const iso = isoOf(year, month, d)
    cells.push({ iso, day: d, inMonth: true, isToday: iso === todayIso })
  }

  let next = 1
  while (cells.length < 42) {
    const m = month === 11 ? 0 : month + 1
    const y = month === 11 ? year + 1 : year
    cells.push({ iso: isoOf(y, m, next), day: next, inMonth: false, isToday: false })
    next++
  }

  return cells
}

export const MONTH_NAMES = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
]

export const WEEKDAY_SHORT = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nd']

/** Short wall-clock, trimming the ':00' Postgres adds. */
export const hhmm = (t: string) => t.slice(0, 5)

/* ── week grid helpers ─────────────────────────────────────────────────── */

/**
 * Where an instant lands on a day column: its calendar day and the number
 * of minutes since midnight, both as seen in `timeZone`. This is the only
 * conversion the week views need — the rest is arithmetic on those two
 * numbers, and never on wall-clock strings.
 */
export function wallClockIn(instant: Date | string, timeZone: string): { day: string; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(instant))
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00'
  // Some engines render midnight as "24" with hour12: false.
  const hour = Number(get('hour')) % 24
  return {
    day: `${get('year')}-${get('month')}-${get('day')}`,
    minutes: hour * 60 + Number(get('minute')),
  }
}

/** Seven ISO dates, Monday first, for the week containing `iso`. */
export function weekDays(iso: string): string[] {
  const d = new Date(`${iso}T00:00:00Z`)
  const lead = (d.getUTCDay() + 6) % 7
  d.setUTCDate(d.getUTCDate() - lead)
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(d)
    day.setUTCDate(d.getUTCDate() + i)
    return day.toISOString().slice(0, 10)
  })
}

export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/** "pon 22" style column heading, in the given UI locale. */
export function weekdayHeading(iso: string, locale = 'pl-PL'): { weekday: string; day: number } {
  const d = new Date(`${iso}T12:00:00Z`)
  return {
    weekday: new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(d),
    day: d.getUTCDate(),
  }
}

/** "22–28 września 2026" / "28 września – 4 października 2026". */
export function weekTitle(days: string[], locale = 'pl-PL'): string {
  const first = new Date(`${days[0]}T12:00:00Z`)
  const last = new Date(`${days[days.length - 1]}T12:00:00Z`)
  const dayNum = (d: Date) => new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: 'UTC' }).format(d)
  const dayMonth = (d: Date) =>
    new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(d)
  const full = (d: Date) =>
    new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(d)
  return first.getUTCMonth() === last.getUTCMonth()
    ? `${dayNum(first)}–${full(last)}`
    : `${dayMonth(first)} – ${full(last)}`
}

export const LOCALE_TAGS: Record<string, string> = { pl: 'pl-PL', uk: 'uk-UA', ru: 'ru-RU' }
